/* eslint-disable no-unused-vars */
import type { Invoice, RepositoryError } from "@invoiceddd/domain";
import { Context, Effect, Option } from "effect";

/**
 * Filters for querying invoices
 * @since 0.1.0
 */
export interface InvoiceFilters {
  readonly invoiceNumber?: string;
  readonly dateFrom?: Date;
  readonly dateTo?: Date;
  readonly minTotal?: number;
  readonly maxTotal?: number;
  readonly createdBy?: string;
}

/**
 * Pagination parameters
 * @since 0.1.0
 */
export interface Pagination {
  readonly page: number;
  readonly limit: number;
}

/**
 * Paginated result for invoice queries
 * @since 0.1.0
 */
export interface PaginatedInvoices {
  readonly invoices: readonly Invoice[];
  readonly totalCount: number;
  readonly page: number;
  readonly limit: number;
  readonly totalPages: number;
}

/**
 * Repository interface for Invoice operations.
 * This port defines the contract for invoice persistence operations.
 *
 * @interface InvoiceRepository
 * @since 0.1.0
 * @see {@link RepositoryError}
 * @see {@link Invoice}
 * @see {@link InvoiceFilters}
 * @see {@link PaginatedInvoices}
 * @example
 * import { InvoiceRepository } from '@invoiceddd/application';
 * // Inject implementation at runtime
 */
export interface InvoiceRepositorySchema {
  /**
   * Save an invoice to the repository.
   * @param invoice The invoice to save
   * @returns Effect that resolves when save is complete
   * @since 0.1.0
   * @example
   * await invoiceRepository.save(invoice)
   */
  readonly save: (_invoice: Invoice) => Effect.Effect<void, RepositoryError>;

  /**
   * Find an invoice by order ID.
   * @param orderId The order ID to search for
   * @returns Effect that resolves to Option containing the invoice if found
   * @since 0.1.0
   * @example
   * const result = await invoiceRepository.findByOrderId('order-123')
   */
  readonly findByOrderId: (
    _orderId: string
  ) => Effect.Effect<Option.Option<Invoice>, RepositoryError>;

  /**
   * Find an invoice by invoice number.
   * @param invoiceNumber The invoice number to search for
   * @returns Effect that resolves to Option containing the invoice if found
   * @since 0.1.0
   * @example
   * const result = await invoiceRepository.findByInvoiceNumber('INV-2025-001')
   */
  readonly findByInvoiceNumber: (
    _invoiceNumber: string
  ) => Effect.Effect<Option.Option<Invoice>, RepositoryError>;

  /**
   * List invoices with filtering and pagination.
   * @param filters Filters to apply to the query
   * @param pagination Pagination parameters
   * @returns Effect that resolves to paginated invoices result
   * @since 0.1.0
   * @example
   * const result = await invoiceRepository.list({ createdBy: 'user' }, { page: 1, limit: 10 })
   */
  readonly list: (
    _filters: InvoiceFilters,
    _pagination: Pagination
  ) => Effect.Effect<PaginatedInvoices, RepositoryError>;
}

/**
 * Context tag for InvoiceRepository service.
 * Used for dependency injection.
 * @since 0.1.0
 * @see {@link InvoiceRepository}
 */
export class InvoiceRepository extends Context.Tag("@/applayer/ports/InvoiceRepository")<
  InvoiceRepository,
  InvoiceRepositorySchema
>() {}
